import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, MessageCircle, FileCheck, Scale, Handshake, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import EnquiryForm from "@/components/EnquiryForm";
import { FadeIn, MaskedLine, Eyebrow } from "@/components/Reveal";
import { HERO_IMAGE, CLAIMS_IMAGE, COMPANY } from "@/data/siteData";
import { useLanguage, useLocalizedContent } from "@/i18n/LanguageContext";

const WHY_ICONS = [Scale, FileCheck, ShieldCheck, Handshake];

export default function Home() {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 800], [0, 180]);
  const { t } = useLanguage();
  const { categories, personal } = useLocalizedContent();

  return (
    <div data-testid="home-page">
      <Seo
        title="Wisdom Insurance Broking | Commercial Insurance Broker Pune"
        description="IRDAI-licensed commercial insurance broker in Pune. Liability, engineering, marine, property and employee benefits insurance for businesses, contractors, manufacturers, co-operative banks and NBFCs."
        keywords="commercial insurance broker Pune, contractors all risk insurance, insurance for co-operative banks, liability insurance India, marine cargo insurance, engineering insurance"
      />

      {/* HERO */}
      <section className="grain relative flex min-h-[100svh] items-center overflow-hidden bg-navy-deep">
        <motion.div style={{ y: yBg }} className="absolute inset-0 scale-110">
          <img src={HERO_IMAGE} alt="" className="h-full w-full object-cover opacity-40" loading="eager" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy-deep/85 to-navy/40" />

        <div className="relative mx-auto w-full max-w-7xl px-4 pb-24 pt-32 sm:px-8 lg:px-16">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-6 font-mono text-xs font-medium uppercase tracking-[0.3em] text-gold"
            data-testid="hero-eyebrow"
          >
            {t("hero.eyebrow")}
          </motion.p>

          <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            <MaskedLine delay={0.35}>{t("hero.h1a")}</MaskedLine>
            <MaskedLine delay={0.5}>{t("hero.h1bPre")} <span className="text-gold">{t("hero.h1bEm")}</span></MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subheading"
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <Link
              to="/contact"
              data-testid="hero-get-quote-btn"
              className="group flex items-center justify-center gap-2 bg-amber-600 px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-amber-500"
            >
              {t("hero.getQuote")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <a
              href={`https://wa.me/${COMPANY.whatsappNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-talk-expert-btn"
              className="flex items-center justify-center gap-2 border border-slate-500 px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:border-gold hover:text-gold"
            >
              <MessageCircle className="h-4 w-4" />
              {t("hero.talkExpert")}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="h-12 w-px bg-gradient-to-b from-transparent via-gold to-transparent" />
        </motion.div>
      </section>

      <Marquee />

      {/* COMMERCIAL GRID */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 md:py-24 lg:px-16" data-testid="commercial-grid-section">
        <FadeIn>
          <Eyebrow>{t("home.gridEyebrow")}</Eyebrow>
          <h2 className="mt-3 max-w-2xl font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
            {t("home.gridTitle")}
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
            {t("home.gridSub")}
          </p>
        </FadeIn>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {categories.map((cat, i) => (
            <FadeIn key={cat.id} delay={i * 0.08}>
              <Link
                to={`/commercial-insurance#${cat.id}`}
                data-testid={`commercial-card-${cat.id}`}
                className="group flex h-full flex-col border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-amber-500/50 hover:shadow-xl"
              >
                <cat.icon className="h-8 w-8 text-amber-600" strokeWidth={1.5} />
                <h3 className="mt-5 font-heading text-xl font-semibold text-navy">{cat.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{cat.blurb}</p>
                <span className="mt-6 flex items-center gap-1.5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-amber-700">
                  {t("home.explore")} <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Link>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* WHY WISDOM — MANIFESTO */}
      <section className="grain relative bg-navy py-16 md:py-24" data-testid="why-wisdom-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <FadeIn>
            <Eyebrow light>{t("home.whyEyebrow")}</Eyebrow>
            <h2 className="mt-3 max-w-2xl font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              {t("home.whyTitle")}
            </h2>
          </FadeIn>
          <div className="mt-12 grid grid-cols-1 gap-px bg-slate-700/40 md:grid-cols-2">
            {WHY_ICONS.map((Icon, i) => (
              <FadeIn key={i} delay={i * 0.08} className="bg-navy p-8 lg:p-10">
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-sm text-gold">{`0${i + 1}`}</span>
                  <Icon className="h-6 w-6 text-slate-500" strokeWidth={1.5} />
                </div>
                <h3 className="mt-6 font-heading text-lg font-semibold text-white sm:text-xl">{t(`why.${i + 1}t`)}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{t(`why.${i + 1}x`)}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CLAIMS HIGHLIGHT */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 md:py-24 lg:px-16" data-testid="claims-highlight-section">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <FadeIn className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -left-3 -top-3 h-full w-full border border-amber-500/40" aria-hidden="true" />
              <img src={CLAIMS_IMAGE} alt="Industrial facility" className="relative h-80 w-full object-cover lg:h-96" loading="lazy" />
            </div>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-6">
            <Eyebrow>{t("home.claimsEyebrow")}</Eyebrow>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              {t("home.claimsTitle")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              {t("home.claimsText")}
            </p>
            <Link
              to="/claims-support"
              data-testid="claims-highlight-cta"
              className="group mt-8 inline-flex items-center gap-2 border-b-2 border-amber-600 pb-1 text-sm font-semibold text-navy transition-colors hover:text-amber-700"
            >
              {t("home.claimsCta")}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* PERSONAL STRIP */}
      <section className="border-y border-slate-200 bg-slate-50 py-12" data-testid="personal-strip-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <FadeIn className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <Eyebrow>{t("home.pStripEyebrow")}</Eyebrow>
              <h2 className="mt-2 font-heading text-lg font-semibold text-navy sm:text-xl">{t("home.pStripTitle")}</h2>
            </div>
            <Link to="/personal-insurance" data-testid="personal-strip-view-all" className="text-sm font-medium text-amber-700 hover:text-amber-800">
              {t("home.viewAll")} →
            </Link>
          </FadeIn>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {personal.map((p, i) => (
              <FadeIn key={p.id} delay={i * 0.06}>
                <Link
                  to="/personal-insurance"
                  data-testid={`personal-strip-${p.id}`}
                  className="flex flex-col items-center gap-3 border border-slate-200 bg-white px-4 py-6 text-center transition-all duration-300 hover:-translate-y-0.5 hover:border-amber-500/50 hover:shadow-md"
                >
                  <p.icon className="h-6 w-6 text-slate-500" strokeWidth={1.5} />
                  <span className="text-sm font-medium text-slate-700">{p.title}</span>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER WITH FORM */}
      <section className="grain relative bg-navy-deep py-16 md:py-24" data-testid="cta-banner-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
          <FadeIn className="lg:col-span-5">
            <Eyebrow light>{t("home.ctaEyebrow")}</Eyebrow>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              {t("home.ctaTitle")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-400">
              {t("home.ctaText")}
            </p>
            <div className="mt-8 space-y-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
              <p>{t("home.ctaP1")}</p>
              <p>{t("home.ctaP2")}</p>
              <p>{t("home.ctaP3")}</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-7">
            <div className="border border-slate-700/60 bg-navy/60 p-6 backdrop-blur-sm sm:p-8">
              <EnquiryForm dark />
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
